import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getAdditionlNewnessFilteringOptions,
  getClearAdditionalNewnessModel,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import TrueFalseImage from "../../components/TrueFalseImage";
import AddOrEditAdditionalNewness from "./components/AddOrEditAdditionalNewness";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function AdditionalNewnessList() {
  const {
    additionalNewnessModel,
    setAdditionalNewnessModel,
    additionalNewnessData,
    ADDITIONAL_NEWNESS_ENDPOINT,
    config,
    pageIndexAdditionalNewness,
    setPageIndexAdditionalNewness,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [additionalNewnessDataFiltered, setAdditionalNewnessDataFiltered] =
    useState(additionalNewnessData.Data);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getAdditionlNewnessFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const additionalNewnessDataObj = additionalNewnessData.Data.find(
        (additionalNewnessObj) => additionalNewnessObj.Id === Number(id)
      );
      setAdditionalNewnessModel(additionalNewnessDataObj);
    }
  }, [id]);

  useEffect(() => {
    setAdditionalNewnessDataFiltered(additionalNewnessData.Data);
  }, [additionalNewnessData]);

  const validateOperationResult = (result, isDelete) => {
    setAdditionalNewnessModel(getClearAdditionalNewnessModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([ADDITIONAL_NEWNESS_ENDPOINT, config]);

    if (isDelete) {
      if (
        pageIndexAdditionalNewness !== 0 &&
        additionalNewnessData.Data.length === 1
      ) {
        setPageIndexAdditionalNewness(pageIndexAdditionalNewness - 1);
      }
    }

    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${ADDITIONAL_NEWNESS_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${ADDITIONAL_NEWNESS_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${ADDITIONAL_NEWNESS_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !additionalNewnessModel.Name ||
    additionalNewnessModel.Name.replaceAll(" ", "").length === 0;

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.need_observation,
      localize.description,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.additional_newness}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={ADDITIONAL_NEWNESS_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="additional_newness"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_additional_newness}
          onClickToSubmit={() => {
            handleUpdateRequest(additionalNewnessModel);
            setShowEditModal(false);
            setAdditionalNewnessModel(getClearAdditionalNewnessModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setAdditionalNewnessModel(getClearAdditionalNewnessModel());
          }}
          Component={
            <AddOrEditAdditionalNewness
              model={additionalNewnessModel}
              setModel={setAdditionalNewnessModel}
            />
          }
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.newness_setup}
            subtitle={localize.newness_setup_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => setShowPrinterModal(true)}
            entityName="additional_newness_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_additional_newness}
                onClickToSubmit={() => {
                  handleCreateRequest(additionalNewnessModel);
                  setShowAddModal(false);
                  setAdditionalNewnessModel(getClearAdditionalNewnessModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setAdditionalNewnessModel(getClearAdditionalNewnessModel());
                }}
                Component={
                  <AddOrEditAdditionalNewness
                    model={additionalNewnessModel}
                    setModel={setAdditionalNewnessModel}
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={additionalNewnessModel.Name}
          entityType={localize.additional_newness}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setAdditionalNewnessModel(getClearAdditionalNewnessModel());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(additionalNewnessModel.Id);
            setShowDeleteModal(false);
            setAdditionalNewnessModel(getClearAdditionalNewnessModel());
          }}
        />
      )}

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexAdditionalNewness}
            setPageIndex={setPageIndexAdditionalNewness}
            dataArray={additionalNewnessData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          additionalNewnessData.Data?.length !== 0
            ? additionalNewnessDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setAdditionalNewnessModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setAdditionalNewnessModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  <TrueFalseImage value={item.NeedObservation} />,
                  <ValueOrNoDataLabel data={item.Description} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(6)]
        }
      />
    </div>
  );
}
